import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '~/components/shadcn/button-simple';
import { CameraCapture } from '~/components/shadcn/camera-capture-simple';
import { Card } from '~/components/shadcn/card-simple';
import { Combobox, type ComboboxOption } from '~/components/shadcn/combobox-simple';
import { Input } from '~/components/shadcn/input-simple';
import { Label } from '~/components/shadcn/label-simple';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/shadcn/table-simple';
import api from '~/services/api';
import history from '~/services/history';

import type { AdditionalItemFormRow, PurchaseOrderItemFormRow, PurchaseOrderRoot, ReceivingPayload, ReceivingPayloadItem } from './types';

const CARRIER_OPTIONS: ComboboxOption[] = [
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'usps', label: 'USPS' },
  { value: 'dhl', label: 'DHL' },
  { value: 'other', label: 'Other' },
];

function toNumber(value?: string | number | null) {
  const nextValue = Number(value ?? 0);
  if (Number.isNaN(nextValue)) return 0;
  return nextValue;
}

export function NewReceivingPurchaseOrder() {
  const { purchaseOrderId } = useParams<{ purchaseOrderId: string }>();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderRoot | null>(null);
  const [trackingCode, setTrackingCode] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [notes, setNotes] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [evidences, setEvidences] = useState<File[]>([]);
  const [purchaseOrderItems, setPurchaseOrderItems] = useState<PurchaseOrderItemFormRow[]>([]);
  const [additionalItems, setAdditionalItems] = useState<AdditionalItemFormRow[]>([]);

  const readOnlyAdditionalItems = useMemo(
    () =>
      (purchaseOrder?.purchase_order_items || [])
        .filter((item) => item.is_additional_item)
        .map((item) => ({
          description: item.description || item.name || '',
          quantity: toNumber(item.quantity),
        }))
        .filter((item) => item.description.trim() && item.quantity > 0),
    [purchaseOrder]
  );

  const orderedReceivableItems = useMemo(() => purchaseOrderItems.filter((item) => item.isPhysicalItem && item.quantity > 0), [purchaseOrderItems]);

  const filledAdditionalItems = useMemo(() => additionalItems.filter((item) => item.description.trim() && item.quantity > 0), [additionalItems]);

  const canEditPhysicalItems = useMemo(() => purchaseOrder?.received?.trim().toLowerCase() === 'pending', [purchaseOrder?.received]);

  useEffect(() => {
    if (!purchaseOrderId) return;

    async function loadPurchaseOrder() {
      setLoading(true);
      try {
        const { data } = await api.get<PurchaseOrderRoot>(`purchaseOrder/get-by-id/${purchaseOrderId}`);
        setPurchaseOrder(data);

        const mappedRows: PurchaseOrderItemFormRow[] = (data.purchase_order_items || [])
          .filter((item) => !item.is_additional_item)
          .map((item) => {
            const quantityOrdered = toNumber(item.quantity);
            const quantityAlreadyReceived = (item.receiving_items || []).reduce((total, receivingItem) => total + toNumber(receivingItem.quantity), 0);

            return {
              purchaseOrderItemId: item.id,
              description: item.description || item.name,
              quantityOrdered,
              quantityAlreadyReceived,
              quantity: 0,
              isPhysicalItem: Boolean(item.is_physical_item),
            };
          });

        setPurchaseOrderItems(mappedRows);
      } catch {
        toast.error('Could not load purchase order data.');
      } finally {
        setLoading(false);
      }
    }

    loadPurchaseOrder();
  }, [purchaseOrderId]);

  function handleEvidenceAdd(event: ChangeEvent<HTMLInputElement>) {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];
    if (!selectedFiles.length) return;

    setEvidences((current) => [...current, ...selectedFiles]);
    event.target.value = '';
  }

  function updatePurchaseOrderItem(index: number, updates: Partial<PurchaseOrderItemFormRow>) {
    setPurchaseOrderItems((current) =>
      current.map((item, currentIndex) => {
        if (currentIndex !== index) return item;
        return { ...item, ...updates };
      })
    );
  }

  function updateAdditionalItem(index: number, updates: Partial<AdditionalItemFormRow>) {
    setAdditionalItems((current) =>
      current.map((item, currentIndex) => {
        if (currentIndex !== index) return item;
        return { ...item, ...updates };
      })
    );
  }

  function removeAdditionalItem(index: number) {
    setAdditionalItems((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  function validateSubmit() {
    if (!trackingCode.trim()) {
      toast.error('Tracking code is required.');
      return false;
    }

    if (!carrierId) {
      toast.error('Carrier is required.');
      return false;
    }

    if (evidences.length === 0) {
      toast.error('At least one evidence is required (upload or camera photo).');
      return false;
    }

    const invalidPurchaseOrderRow = purchaseOrderItems.find((item) => {
      if (!item.isPhysicalItem) return false;
      if (item.quantity < 0) return true;
      const maxReceivable = Math.max(0, item.quantityOrdered - item.quantityAlreadyReceived);
      return item.quantity > maxReceivable;
    });

    if (invalidPurchaseOrderRow) {
      toast.error(`Invalid quantity for item: ${invalidPurchaseOrderRow.description}`);
      return false;
    }

    const invalidAdditionalItem = additionalItems.find((item) => item.description.trim() && item.quantity <= 0);
    if (invalidAdditionalItem) {
      toast.error(`Additional item "${invalidAdditionalItem.description}" must have quantity greater than 0.`);
      return false;
    }

    if (orderedReceivableItems.length === 0 && filledAdditionalItems.length === 0) {
      toast.error('Please receive at least one item (PO item or additional item).');
      return false;
    }

    return true;
  }

  async function handleSave() {
    if (!purchaseOrderId || !purchaseOrder) return;
    if (!validateSubmit()) return;

    const receivingItemsPayload: ReceivingPayloadItem[] = purchaseOrderItems.map((item) => ({
      purchaseOrderItemId: item.purchaseOrderItemId,
      description: item.description,
      quantityToReceive: Math.max(0, item.quantityOrdered - item.quantityAlreadyReceived),
      quantityAlreadyReceived: item.quantityAlreadyReceived,
      quantity: item.quantity,
      isPhysicalItem: item.isPhysicalItem,
      additionalItem: false,
    }));

    const payload: ReceivingPayload = {
      trackingCode: trackingCode.trim(),
      type: 'PO',
      purchaseOrderId: Number(purchaseOrderId),
      supplierId: null,
      carrierId,
      notes: notes.trim() || null,
      receivingItems: [...receivingItemsPayload],
      additionalItems: filledAdditionalItems,
    };

    const formData = new FormData();
    evidences.forEach((file) => {
      formData.append('pictures', file);
    });
    formData.append('data', JSON.stringify(payload));

    setSubmitting(true);
    try {
      await api.post('new-receiving', formData);
      toast.success('Receiving created successfully.');
      history.push('/new-receiving');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Could not create receiving.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground xl:pl-60">
      <main className="px-4 pb-6 pt-20 lg:px-6 lg:pb-8">
        <section className="rounded-lg border border-border/70 bg-background/95 p-4 shadow-sm lg:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-xl font-semibold tracking-tight">New Receiving - Purchase Order</h1>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => history.push('/new-receiving')}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading || submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>

          {loading ? <p className="text-sm text-muted-foreground">Loading purchase order...</p> : null}

          <Card className="mb-4 p-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-1">
                <Label htmlFor="po-number">P.O. Number</Label>
                <Input id="po-number" value={purchaseOrder?.po_number || ''} disabled />
              </div>

              <div className="space-y-1">
                <Label htmlFor="tracking-code">Tracking Code *</Label>
                <Input id="tracking-code" value={trackingCode} onChange={(event) => setTrackingCode(event.target.value)} placeholder="Enter tracking code" />
              </div>

              <div className="space-y-1">
                <Label htmlFor="carrier">Carrier *</Label>
                <Combobox id="carrier" value={carrierId} options={CARRIER_OPTIONS} onChange={setCarrierId} placeholder="Select carrier" />
              </div>

              <div className="space-y-1">
                <Label htmlFor="supplier">Supplier</Label>
                <Input id="supplier" value={purchaseOrder?.supplier?.name || ''} disabled />
              </div>
            </div>
          </Card>

          <Card className="mb-4 p-3">
            <h2 className="mb-3 text-base font-semibold">Purchase Order Items</h2>
            <Table className="rounded-xl border border-border/70 bg-background shadow-sm [&_table]:min-w-full">
              <TableHeader className="bg-muted/35">
                <TableRow>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">P.O. Item</TableHead>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Qty Ordered</TableHead>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Qty Already Received</TableHead>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Quantity</TableHead>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Physical Item</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchaseOrderItems.map((item, index) => {
                  const maxReceivable = Math.max(0, item.quantityOrdered - item.quantityAlreadyReceived);
                  const isFullyReceived = item.quantityOrdered <= item.quantityAlreadyReceived;
                  const doesNotNeedReceiving = !item.isPhysicalItem || isFullyReceived;
                  const noReceivingReason = !item.isPhysicalItem ? 'Non-physical item' : 'Fully received';

                  return (
                    <TableRow key={item.purchaseOrderItemId} className={doesNotNeedReceiving ? 'bg-amber-50 hover:bg-amber-50/90' : ''}>
                      <TableCell className="px-2 py-1.5 text-sm">
                        <div className="flex items-center gap-2">
                          <span className={doesNotNeedReceiving ? 'text-muted-foreground' : ''}>{item.description}</span>
                          {doesNotNeedReceiving ? (
                            <span className="inline-flex rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                              {noReceivingReason}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-1.5 text-sm">{item.quantityOrdered}</TableCell>
                      <TableCell className="px-2 py-1.5 text-sm">{item.quantityAlreadyReceived}</TableCell>
                      <TableCell className="max-w-[120px] px-2 py-1.5">
                        <Input
                          type="number"
                          min={0}
                          max={maxReceivable}
                          className="h-8"
                          disabled={!item.isPhysicalItem || maxReceivable === 0}
                          value={item.quantity}
                          onChange={(event) => {
                            updatePurchaseOrderItem(index, {
                              quantity: Math.max(0, toNumber(event.target.value)),
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell className="px-2 py-1.5">
                        <label className="inline-flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5"
                            checked={item.isPhysicalItem}
                            disabled={!canEditPhysicalItems}
                            onChange={(event) => {
                              updatePurchaseOrderItem(index, {
                                isPhysicalItem: event.target.checked,
                                quantity: event.target.checked ? item.quantity : 0,
                              });
                            }}
                          />
                          {item.isPhysicalItem ? 'Yes' : 'No'}
                        </label>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          <Card className="mb-4 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold">Additional Items</h2>
              <Button
                variant="outline"
                onClick={() => {
                  setAdditionalItems((current) => [...current, { description: '', quantity: 0 }]);
                }}
              >
                Add additional item
              </Button>
            </div>

            <div className="space-y-2">
              {readOnlyAdditionalItems.length > 0 ? (
                <div className="space-y-2">
                  {/* <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">From Purchase Order (read-only)</p> */}
                  <div className="max-h-40 space-y-1 overflow-auto rounded-md border border-border/70 bg-muted/15 p-2">
                    {readOnlyAdditionalItems.map((item, index) => (
                      <div key={`po-additional-item-${index + 1}`} className="py-0.5 text-sm">
                        <span className="text-foreground/90">{item.description}</span>
                        <span className="ml-1.5 text-xs text-muted-foreground">(Qty: {item.quantity})</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* {additionalItems.length > 0 ? <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Manual Additional Items</p> : null} */}
              {additionalItems.map((item, index) => (
                <div key={`additional-item-${index + 1}`} className="flex items-center gap-2 rounded-md border border-border/70 p-3">
                  <Input
                    className="min-w-[220px] flex-1"
                    value={item.description}
                    placeholder="Item description"
                    onChange={(event) => {
                      updateAdditionalItem(index, { description: event.target.value });
                    }}
                  />
                  <Input
                    className="h-10 w-28 shrink-0"
                    type="number"
                    min={0}
                    value={item.quantity}
                    placeholder="Quantity"
                    onChange={(event) => {
                      updateAdditionalItem(index, { quantity: Math.max(0, toNumber(event.target.value)) });
                    }}
                  />
                  <Button
                    variant="outline"
                    className="shrink-0"
                    onClick={() => {
                      removeAdditionalItem(index);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}

              {readOnlyAdditionalItems.length === 0 && additionalItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No additional items.</p>
              ) : null}
            </div>
          </Card>

          <Card className="mb-4 p-4">
            <h2 className="mb-3 text-base font-semibold">Evidence *</h2>
            <div className="mb-3 flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  uploadInputRef.current?.click();
                }}
              >
                Upload Files
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setCameraOpen(true);
                }}
              >
                Take Photo
              </Button>
            </div>

            <input ref={uploadInputRef} type="file" className="hidden" multiple onChange={handleEvidenceAdd} />

            <div className="space-y-2">
              {evidences.length === 0 ? <p className="text-sm text-muted-foreground">No evidence attached yet.</p> : null}
              {evidences.map((file, index) => (
                <div key={`${file.name}-${index + 1}`} className="flex items-center justify-between rounded-md border border-border/70 px-3 py-2 text-sm">
                  <span className="truncate pr-2">{file.name}</span>
                  <Button
                    variant="ghost"
                    className="h-8 px-2"
                    onClick={() => {
                      setEvidences((current) => current.filter((_, currentIndex) => currentIndex !== index));
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-4">
            <Label htmlFor="notes" className="mb-1 block">
              Notes (optional)
            </Label>
            <Input id="notes" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Add notes" />
          </Card>

          <CameraCapture
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCapture={(photo) => {
              setEvidences((current) => [...current, photo]);
            }}
          />
        </section>
      </main>
    </div>
  );
}
